import { useCallback, useEffect, useRef, useState } from 'react'
import { Document, Page, pdfjs } from 'react-pdf'
import 'react-pdf/dist/Page/AnnotationLayer.css'
import 'react-pdf/dist/Page/TextLayer.css'
import {
  AlertTriangle,
  ChevronLeft,
  ChevronRight,
  Download,
  Eye,
  FileText,
  Loader2,
  MoreHorizontal,
  Star,
  Trash,
  Upload,
  SquarePen,
  X,
  ZoomIn,
  ZoomOut,
} from 'lucide-react'
import { toast } from 'sonner'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useExportTemplatesStore } from '@/stores/export-templates-store'
import { useAuthStore } from '@/stores/auth-store'
import { ApiRequestError, getExportTemplatePreviewUrl, toastApiError } from '@/lib/api'
import type { ExportTemplate } from '@/lib/api'
import { TemplateCheckSummary } from '@/components/settings/TemplateCheckSummary'
import { isUnsafePlaceholder, templateFixHowTo } from '@/lib/template-check'

// Configure pdf.js worker (same as DocumentPreviewModal)
if (!pdfjs.GlobalWorkerOptions.workerSrc) {
  pdfjs.GlobalWorkerOptions.workerSrc = new URL(
    'pdfjs-dist/build/pdf.worker.min.mjs',
    import.meta.url
  ).toString()
}

// ===================
// Constants
// ===================

const MAX_TEMPLATES = 5
const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024 // 10MB
const POLL_INTERVAL_MS = 3000
const ZOOM_STEP = 0.25
const MIN_ZOOM = 0.5
const MAX_ZOOM = 3

// ===================
// Helpers
// ===================

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

function formatRelativeDate(dateStr: string): string {
  const date = new Date(dateStr)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))

  if (diffDays === 0) return 'Today'
  if (diffDays === 1) return 'Yesterday'
  if (diffDays < 7) return `${diffDays} days ago`
  if (diffDays < 30) return `${Math.floor(diffDays / 7)} week${Math.floor(diffDays / 7) > 1 ? 's' : ''} ago`
  return date.toLocaleDateString()
}

// ===================
// Component
// ===================

export function ExportTemplatesTab() {
  const role = useAuthStore((s) => s.role)
  const isAdmin = role === 'admin'

  const {
    templates,
    isLoading,
    fetchTemplates,
    uploadTemplate,
    rename,
    remove,
    setDefault,
    downloadTemplate,
    fixGuide,
    setFixGuide,
  } = useExportTemplatesStore()

  const [showUploadDialog, setShowUploadDialog] = useState(false)
  const [deleteTemplateId, setDeleteTemplateId] = useState<string | null>(null)
  const [renameTemplateId, setRenameTemplateId] = useState<string | null>(null)
  const [renameValue, setRenameValue] = useState('')
  const [previewTemplate, setPreviewTemplate] = useState<ExportTemplate | null>(null)

  useEffect(() => {
    void fetchTemplates()
  }, [fetchTemplates])

  const atLimit = templates.length >= MAX_TEMPLATES

  const handleDelete = useCallback(async () => {
    if (!deleteTemplateId) return
    try {
      await remove(deleteTemplateId)
      toast.success('Template deleted')
    } catch (err) {
      toastApiError(err, 'Failed to delete template')
    } finally {
      setDeleteTemplateId(null)
    }
  }, [deleteTemplateId, remove])

  const handleRename = useCallback(async () => {
    if (!renameTemplateId || !renameValue.trim()) return
    try {
      await rename(renameTemplateId, renameValue.trim())
      toast.success('Template renamed')
    } catch (err) {
      toastApiError(err, 'Failed to rename template')
    } finally {
      setRenameTemplateId(null)
      setRenameValue('')
    }
  }, [renameTemplateId, renameValue, rename])

  const handleSetDefault = useCallback(
    async (id: string) => {
      try {
        await setDefault(id)
        toast.success('Default template updated')
      } catch (err) {
        toastApiError(err, 'Failed to set default')
      }
    },
    [setDefault],
  )

  const handleDownload = useCallback(
    async (id: string) => {
      try {
        await downloadTemplate(id)
      } catch (err) {
        toastApiError(err, 'Download failed')
      }
    },
    [downloadTemplate],
  )

  if (isLoading && templates.length === 0) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    )
  }

  return (
    <>
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-medium">Export Templates</h2>
        {isAdmin && (
          <Button
            size="sm"
            onClick={() => setShowUploadDialog(true)}
            disabled={atLimit}
            title={atLimit ? 'Maximum 5 templates reached. Delete one to upload another.' : undefined}
          >
            <Upload className="h-4 w-4 mr-2" />
            Upload Template
          </Button>
        )}
      </div>

      {/* How-to from the export warning's "Fix / Add the placeholder" */}
      {fixGuide && (
        <div role="status" className="flex items-start gap-2 rounded-lg bg-muted/40 p-3">
          <div className="min-w-0 flex-1 space-y-1">
            <p className="text-sm font-medium text-foreground">To fix "{fixGuide.templateName}"</p>
            <p className="text-sm text-foreground">{fixGuide.howTo}</p>
          </div>
          <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0" aria-label="Dismiss" onClick={() => setFixGuide(null)}>
            <X className="h-4 w-4" />
          </Button>
        </div>
      )}

      {/* Template List */}
      {templates.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <FileText className="mx-auto mb-4 h-12 w-12 text-muted-foreground" />
          <h3 className="text-base font-medium mb-1">No export templates yet</h3>
          {/* The instruction and the control that satisfies it must be gated
              together. Telling a member to upload and then rendering no upload
              button reads as "this is broken" — state the restriction plainly,
              as the sibling admin-gated tabs do. */}
          {isAdmin ? (
            <>
              <p className="text-sm text-muted-foreground mb-4 max-w-sm">
                Upload a branded Word document to use as a template when exporting responses.
              </p>
              <Button onClick={() => setShowUploadDialog(true)}>
                <Upload className="h-4 w-4 mr-2" />
                Upload Template
              </Button>
            </>
          ) : (
            <p className="text-sm text-muted-foreground max-w-sm">
              Export templates are branded Word documents used when exporting responses.
              Only organisation admins can upload them.
            </p>
          )}
        </div>
      ) : (
        <div className="bg-card border rounded-lg divide-y">
          {templates.map((template) => (
            <div
              key={template.id}
              className="flex items-center justify-between px-4 py-3"
            >
              <button
                type="button"
                className="flex items-center gap-3 min-w-0 text-left hover:opacity-70 transition-opacity cursor-pointer"
                onClick={() => setPreviewTemplate(template)}
              >
                <FileText className="h-5 w-5 text-muted-foreground shrink-0" />
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium truncate">
                      {template.name}
                    </span>
                    {template.isDefault && (
                      <span className="flex items-center gap-1 text-xs text-amber-600">
                        <Star className="h-3 w-3 fill-amber-500 text-amber-500" />
                        Default
                      </span>
                    )}
                    {template.hasPlaceholder && (
                      <span className="text-meta bg-muted px-1.5 py-0.5 rounded-lg">
                        Has content placeholder
                      </span>
                    )}
                  </div>
                  <p className="text-meta">
                    {template.originalFilename} · {formatFileSize(template.fileSizeBytes)} · {formatRelativeDate(template.createdAt)}
                  </p>
                  <TemplateCheckLine template={template} />
                </div>
              </button>

              {isAdmin && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0">
                      <MoreHorizontal className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => setPreviewTemplate(template)}>
                      <Eye className="h-4 w-4 mr-2" />
                      Preview
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => {
                        setRenameTemplateId(template.id)
                        setRenameValue(template.name)
                      }}
                    >
                      <SquarePen className="h-4 w-4 mr-2" />
                      Rename
                    </DropdownMenuItem>
                    {!template.isDefault && (
                      <DropdownMenuItem onClick={() => void handleSetDefault(template.id)}>
                        <Star className="h-4 w-4 mr-2" />
                        Set as Default
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuItem onClick={() => void handleDownload(template.id)}>
                      <Download className="h-4 w-4 mr-2" />
                      Download
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      className="text-destructive focus:text-destructive"
                      onClick={() => setDeleteTemplateId(template.id)}
                    >
                      <Trash className="h-4 w-4 mr-2" />
                      Delete
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
          ))}
        </div>
      )}

      {templates.length > 0 && (
        <p className="text-meta">
          {templates.length} of {MAX_TEMPLATES} templates used
        </p>
      )}

      {/* Upload Dialog */}
      <UploadTemplateDialog
        open={showUploadDialog}
        onOpenChange={setShowUploadDialog}
        onUpload={uploadTemplate}
        onRemove={remove}
      />

      {/* Rename Dialog */}
      <Dialog
        open={renameTemplateId !== null}
        onOpenChange={(open) => {
          if (!open) {
            setRenameTemplateId(null)
            setRenameValue('')
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Rename Template</DialogTitle>
            <DialogDescription>Enter a new name for this template.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="rename-input" className="text-meta">
              Template Name
            </Label>
            <Input
              id="rename-input"
              value={renameValue}
              onChange={(e) => setRenameValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  void handleRename()
                }
              }}
            />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setRenameTemplateId(null)}>
              Cancel
            </Button>
            <Button onClick={() => void handleRename()} disabled={!renameValue.trim()}>
              Rename
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Template Preview */}
      <TemplatePreviewDialog
        template={previewTemplate}
        onClose={() => setPreviewTemplate(null)}
        onDownload={handleDownload}
      />

      {/* Delete Confirmation */}
      <AlertDialog
        open={deleteTemplateId !== null}
        onOpenChange={(open) => {
          if (!open) setDeleteTemplateId(null)
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Template</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete this export template. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => void handleDelete()}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}

// ===================
// Template check line (list row)
// ===================

/** One quiet line under a template that its exports will stop and ask about. */
function TemplateCheckLine({ template }: { template: ExportTemplate }) {
  const report = template.checkReport
  if (!report) return null
  const text = !report.needsAcknowledgement
    ? null
    : isUnsafePlaceholder(report)
      ? 'The content placeholder is in the wrong place: exports take template text with it, and ask first.'
      : 'No content placeholder: exports replace part of this template, and ask first.'
  if (!text) return null
  return (
    <p className="mt-0.5 flex items-center gap-1 text-meta">
      <AlertTriangle aria-hidden className="h-3 w-3 shrink-0 text-[hsl(var(--status-warning))]" />
      {text}
    </p>
  )
}

// ===================
// Upload Dialog
// ===================

function UploadTemplateDialog({
  open,
  onOpenChange,
  onUpload,
  onRemove,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  onUpload: (name: string, file: File) => Promise<ExportTemplate>
  onRemove: (id: string) => Promise<void>
}) {
  const [name, setName] = useState('')
  const [file, setFile] = useState<File | null>(null)
  const [isUploading, setIsUploading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // After upload: the template and its check report (R3 T1).
  const [checked, setChecked] = useState<ExportTemplate | null>(null)
  const [isRemoving, setIsRemoving] = useState(false)
  // Set when the user chose to fix the template and upload it again.
  const [howTo, setHowTo] = useState<string | null>(null)

  // Reset state when dialog opens
  useEffect(() => {
    if (open) {
      setName('')
      setFile(null)
      setIsUploading(false)
      setError(null)
      setChecked(null)
      setIsRemoving(false)
      setHowTo(null)
    }
  }, [open])

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files?.[0]
    if (!selected) return

    setError(null)

    if (!selected.name.endsWith('.docx')) {
      setError('Only .docx files are supported.')
      return
    }

    if (selected.size > MAX_FILE_SIZE_BYTES) {
      setError('File too large. Maximum template size is 10MB.')
      return
    }

    setFile(selected)
    // Pre-fill name from filename (without extension) if name is empty
    if (!name) {
      setName(selected.name.replace(/\.docx$/i, ''))
    }
  }

  const handleUpload = async () => {
    if (!file || !name.trim()) return
    setIsUploading(true)
    setError(null)
    try {
      const template = await onUpload(name.trim(), file)
      toast.success('Template uploaded successfully')
      if (template.checkReport) {
        setChecked(template)
      } else {
        onOpenChange(false)
      }
    } catch (err) {
      setError(
        err instanceof ApiRequestError
          ? err.message
          : 'Upload failed. Please try again.',
      )
    } finally {
      setIsUploading(false)
    }
  }

  // "Add the placeholder": the uploaded copy is removed so the fixed file can
  // take its name; the dialog goes back to choosing a file.
  const handleAddPlaceholder = async () => {
    if (!checked) return
    setIsRemoving(true)
    try {
      await onRemove(checked.id)
      if (checked.checkReport) setHowTo(templateFixHowTo(checked.checkReport))
      setChecked(null)
      setFile(null)
    } catch (err) {
      toastApiError(err, 'Failed to remove the template')
    } finally {
      setIsRemoving(false)
    }
  }

  const report = checked?.checkReport ?? null
  if (checked && report) {
    const asksToFix = report.needsAcknowledgement
    const unsafe = isUnsafePlaceholder(report)
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Template check</DialogTitle>
            <DialogDescription>What happens when you export with "{checked.name}".</DialogDescription>
          </DialogHeader>

          <div className="max-h-[55vh] overflow-y-auto">
            <TemplateCheckSummary report={report} />
          </div>

          <DialogFooter>
            {asksToFix ? (
              <>
                <Button variant="ghost" onClick={() => void handleAddPlaceholder()} disabled={isRemoving}>
                  {isRemoving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                  {unsafe ? 'Fix the placeholder' : 'Add the placeholder'}
                </Button>
                <Button onClick={() => onOpenChange(false)} disabled={isRemoving}>
                  Continue
                </Button>
              </>
            ) : (
              <Button onClick={() => onOpenChange(false)}>Done</Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    )
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Upload Export Template</DialogTitle>
          <DialogDescription>
            Upload a branded Word document (.docx) to use as a template when exporting responses.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {howTo && (
            <p className="rounded-lg bg-muted/40 p-3 text-sm text-foreground">{howTo}</p>
          )}

          <div className="space-y-2">
            <Label htmlFor="template-name" className="text-meta">
              Template Name
            </Label>
            <Input
              id="template-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Standard Response Template"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="template-file" className="text-meta">
              File
            </Label>
            {file ? (
              <div className="flex items-center gap-2 px-3 py-2 border rounded-lg bg-muted/50">
                <FileText className="h-4 w-4 text-muted-foreground shrink-0" />
                <span className="text-sm truncate">{file.name}</span>
                <span className="text-meta shrink-0">
                  ({formatFileSize(file.size)})
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  className="ml-auto h-6 px-2 text-xs"
                  onClick={() => setFile(null)}
                >
                  Change
                </Button>
              </div>
            ) : (
              <div>
                <input
                  id="template-file"
                  type="file"
                  accept=".docx"
                  onChange={handleFileChange}
                  className="block w-full text-sm text-muted-foreground file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:bg-muted file:text-foreground hover:file:bg-muted/80 cursor-pointer"
                />
              </div>
            )}
            <p className="text-meta">
              Only .docx files up to 10MB. Type BIDCONTEXT_CONTENT on its own line where the response should go.
            </p>
          </div>

          {error && (
            <p className="text-sm text-destructive">{error}</p>
          )}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={isUploading}>
            Cancel
          </Button>
          <Button
            onClick={() => void handleUpload()}
            disabled={!file || !name.trim() || isUploading}
          >
            {isUploading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                Uploading...
              </>
            ) : (
              'Upload'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// ===================
// Template Preview Dialog (PDF via react-pdf)
// ===================

function TemplatePreviewDialog({
  template,
  onClose,
  onDownload,
}: {
  template: ExportTemplate | null
  onClose: () => void
  onDownload: (id: string) => Promise<void>
}) {
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [status, setStatus] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [numPages, setNumPages] = useState<number | null>(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [zoom, setZoom] = useState(1)
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null)

  // Fetch preview URL when template changes
  useEffect(() => {
    if (!template) {
      setPreviewUrl(null)
      setStatus(null)
      setError(null)
      setNumPages(null)
      setCurrentPage(1)
      setZoom(1)
      return
    }

    let cancelled = false

    async function fetchPreview() {
      try {
        const result = await getExportTemplatePreviewUrl(template!.id)
        if (cancelled) return

        setStatus(result.status)

        if (result.status === 'ready' && result.previewUrl) {
          setPreviewUrl(result.previewUrl)
          // Stop polling
          if (pollRef.current) {
            clearInterval(pollRef.current)
            pollRef.current = null
          }
        } else if (result.status === 'failed') {
          setError('Preview generation failed. You can download the template instead.')
          if (pollRef.current) {
            clearInterval(pollRef.current)
            pollRef.current = null
          }
        }
        // If pending/processing, polling continues
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load preview')
        }
      }
    }

    // Initial fetch
    void fetchPreview()

    // Start polling if not immediately ready
    pollRef.current = setInterval(() => {
      if (!cancelled) void fetchPreview()
    }, POLL_INTERVAL_MS)

    return () => {
      cancelled = true
      if (pollRef.current) {
        clearInterval(pollRef.current)
        pollRef.current = null
      }
    }
  }, [template])

  const isLoading = !previewUrl && !error && status !== 'failed'

  return (
    <Dialog open={template !== null} onOpenChange={(open) => { if (!open) onClose() }}>
      <DialogContent className="max-w-[95vw] w-[95vw] h-[95vh] max-h-[95vh] p-0 gap-0 flex flex-col [&>button.absolute]:hidden">
        {/* Header — matches DocumentPreviewModal layout */}
        <div className="flex items-center justify-between border-b px-4 py-2 shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            <FileText className="h-4 w-4 text-muted-foreground shrink-0" />
            <div className="min-w-0">
              <DialogTitle className="text-sm font-semibold truncate">
                {template?.name}
              </DialogTitle>
              <DialogDescription className="text-meta">
                {template?.originalFilename}
                {template && <span> · {formatFileSize(template.fileSizeBytes)}</span>}
                {numPages != null && <span> · {numPages} pages</span>}
              </DialogDescription>
            </div>
          </div>

          <div className="flex items-center gap-1">
            {/* Zoom controls */}
            {previewUrl && (
              <>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setZoom((z) => Math.max(MIN_ZOOM, z - ZOOM_STEP))}
                  disabled={zoom <= MIN_ZOOM}
                >
                  <ZoomOut className="h-4 w-4" />
                </Button>
                <span className="text-meta w-12 text-center">
                  {Math.round(zoom * 100)}%
                </span>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setZoom((z) => Math.min(MAX_ZOOM, z + ZOOM_STEP))}
                  disabled={zoom >= MAX_ZOOM}
                >
                  <ZoomIn className="h-4 w-4" />
                </Button>
                <div className="w-px h-4 bg-border mx-1" />
              </>
            )}

            {/* Page navigation */}
            {numPages != null && numPages > 1 && (
              <>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setCurrentPage((p) => p - 1)}
                  disabled={currentPage <= 1}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="text-meta w-16 text-center">
                  {currentPage} / {numPages}
                </span>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setCurrentPage((p) => p + 1)}
                  disabled={currentPage >= numPages}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
                <div className="w-px h-4 bg-border mx-1" />
              </>
            )}

            <Button variant="ghost" size="icon" onClick={() => template && void onDownload(template.id)}>
              <Download className="h-4 w-4" />
            </Button>
            <Button variant="ghost" size="icon" onClick={onClose}>
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto flex items-start justify-center bg-muted/30 p-4">
          {isLoading && (
            <div className="flex flex-col items-center justify-center h-full gap-2">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              <p className="text-sm text-muted-foreground">
                {status === 'processing' || status === 'pending'
                  ? 'Generating preview...'
                  : 'Loading preview...'}
              </p>
            </div>
          )}

          {error && (
            <div className="flex flex-col items-center justify-center h-full gap-3">
              <div className="h-12 w-12 rounded-full bg-destructive/10 flex items-center justify-center">
                <AlertTriangle className="h-6 w-6 text-destructive" />
              </div>
              <p className="text-sm text-destructive">{error}</p>
              <Button variant="ghost" onClick={() => template && void onDownload(template.id)}>
                <Download className="h-4 w-4 mr-2" />
                Download instead
              </Button>
            </div>
          )}

          {previewUrl && (
            <Document
              file={previewUrl}
              onLoadSuccess={({ numPages: pages }) => setNumPages(pages)}
              onLoadError={(err) => setError(err.message || 'Failed to load PDF')}
              loading={
                <div className="flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                  <span className="text-sm text-muted-foreground">Loading PDF...</span>
                </div>
              }
            >
              <Page
                pageNumber={currentPage}
                scale={zoom}
                className="shadow-lg"
                loading={
                  <div className="h-[800px] w-[600px] bg-muted animate-pulse rounded" />
                }
              />
            </Document>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
